import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  applyNodeChanges,
  Edge,
  Node,
  applyEdgeChanges,
  addEdge,
  ConnectionMode,
  BackgroundVariant,
  ReactFlowProps,
  useKeyPress,
  NodeChange,
  EdgeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {useCallback, useEffect, useState} from 'react';
import './style.css';
import {
  PopoverWithAnchor,
  usePopoverContextMenu,
  UsePopoverContextMenuReturn,
} from '../components/popover-context-menu';
import {WorkflowContextMenu} from './workflow-context-menu';
import useDialogState, {UseDialogStateReturn} from '@/hooks/useDialogState';
import {nodeFeatures, nodeTypes} from './nodes';
import useWorkflowStore from './store';
import {Resizable} from 're-resizable';
import {cn} from '@/lib/tailwind-utils';
import {defaultEdge} from './const';
import {edgeTypes} from './edges';
import {EdgeStyleSetting} from './toolbar/edge-style-set';
import {Popover, PopoverAnchor, PopoverContent} from '@/shadcn_components/ui/popover';
import {TimelineMarkers} from './timeline-markers';
import { Swimlanes } from './swimlanes';
import { MyControls } from './my-controls';

export type WorkflowInputProps = {reactflowProps?: Partial<ReactFlowProps>} & Partial<
  React.ComponentProps<'div'>
>;

function ProjectWorkflow(props: WorkflowInputProps) {
  const {
    nodes,
    setNodes,
    edges,
    setEdges,
    viewport,
    setViewport,
    setChange, // for dirty objects
    setDialogState,
    takeSnapshot,
    undo,
    redo,
    selectedEdges,
    setSelectedEdges,
  } = useWorkflowStore();

  // Handle undo/redo keyboard shortcuts

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+Z or Command+Z (undo)
      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.preventDefault();
        undo();
      }
      // Check for Ctrl+Y or Command+Y (redo)
      else if ((event.ctrlKey || event.metaKey) && event.key === 'y') {
        event.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo, redo]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Take snapshot before applying changes
      if (changes.some(change => ['add', 'dimensions', 'remove'].includes(change.type))) {
        takeSnapshot();
      }
      setNodes(applyNodeChanges(changes, nodes));
    },
    [nodes, setNodes, takeSnapshot],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // Take snapshot before applying changes
      if (changes.some(change => change.type === 'remove')) {
        takeSnapshot();
      }
      setEdges(applyEdgeChanges(changes, edges));
    },
    [edges, setEdges, takeSnapshot],
  );

  const onConnect = useCallback(
    connection => {
      takeSnapshot();
      setEdges(addEdge({...connection, ...defaultEdge}, edges));
    },
    [edges, setEdges, takeSnapshot],
  );

  const onBeforeDelete = useCallback(
    async (elements: {nodes: Node[]; edges: Edge[]}) => {
      // Take snapshot before deletion
      // takeSnapshot();

      const startNodes = elements.nodes.filter(n => n.type === 'start');
      const nonStartNodes = elements.nodes.filter(n => n.type !== 'start');
      const startNodeIds = new Set(startNodes.map(n => n.id));

      // Filter out edges connected to start nodes
      const remainingEdges = elements.edges.filter(
        e => !startNodeIds.has(e.source) && !startNodeIds.has(e.target),
      );

      // Return filtered elements
      return {
        nodes: nonStartNodes,
        edges: startNodes.length > 0 ? remainingEdges : elements.edges,
      };
    },
    [nodes, setNodes, edges, setEdges, takeSnapshot],
  );

  const onNodesDelete = useCallback(
    (removes: Node[]) => {
      console.log('deleted nodes', removes);
      removes.forEach(node => {
        setChange('nodes', node.id, 'deleted');
      });
      setNodes(nodes.filter(n => !removes.find(v => v.id == n.id)));
    },
    [nodes, setNodes],
  );

  ///////// node features
  const createDialogState = useDialogState();
  const editDialogState = useDialogState();
  useEffect(() => {
    setDialogState('createNode', createDialogState);
    setDialogState('editNode', editDialogState);
  }, []);

  // edit dialog
  const onNodeDoubleClick = useCallback((event, node: Node) => {
    if (!node.type || !nodeTypes[node.type]) return;
    // const node_id = node.id;
    // setSelectedNode(node);
    editDialogState.openDialog(node);
    // console.log('double click', node);
  }, []);

  // node context menu
  const nodeCtxMenu = usePopoverContextMenu();
  const onNodeContextMenu = useCallback((event, node: Node) => {
    console.log('node context menu', node);
    if (!node.type || !nodeTypes[node.type]) return;
    nodeCtxMenu.onContextMenu(event, node);
  }, []);

  //////// workflow context menu
  const wfContextMenu = usePopoverContextMenu();

  /////// edge context menu
  const edgeCtxMenu = usePopoverContextMenu();

  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      // When a new edge is context-clicked, add it to the selectedEdges array
      if (!selectedEdges.find(e => e.id === edge.id)) {
        setSelectedEdges([...selectedEdges, edge]);
      }
      edgeCtxMenu.onContextMenu(event, edge);
    },
    [selectedEdges, setSelectedEdges],
  );

  const onEdgeStyleChange = useCallback(
    (style: Partial<Edge>) => {
      if (selectedEdges.length === 0) return;

      takeSnapshot();

      const newEdges = edges.map(edge => {
        if (selectedEdges.find(e => e.id === edge.id)) {
          return {...edge, ...style, style: {...edge.style, ...style.style}};
        }
        return edge;
      });

      setEdges(newEdges);
      // update selected edges for consistency
      setSelectedEdges(newEdges.filter(e => selectedEdges.find(se => se.id === e.id)));
    },
    [selectedEdges, setEdges, takeSnapshot],
  );

  function renderEdgeContextMenu(
    edgeCtxMenu: UsePopoverContextMenuReturn,
    selectedEdges: Edge[],
    onEdgeStyleChange: (style: Partial<Edge>) => void,
  ) {
    const state = edgeCtxMenu.state;
    const anchor = edgeCtxMenu.anchor;
    if (!state.open) return null;
    if (!anchor) return null;

    return (
      <PopoverWithAnchor {...edgeCtxMenu}>
        <div className="draggable-handle w-full h-4 bg-secondary flex items-center justify-center cursor-grab border-b border-border hover:bg-secondary/80 active:cursor-grabbing">
          <div className="w-8 h-2 flex flex-col justify-between items-center">
            <div className="w-full h-[1px] bg-muted-foreground rounded-full"></div>
            <div className="w-full h-[1px] bg-muted-foreground rounded-full"></div>
          </div>
        </div>
        <EdgeStyleSetting selectedEdges={selectedEdges} onEdgeStyleChange={onEdgeStyleChange} />
      </PopoverWithAnchor>
    );
  }

  /////// render functions
  function renderNodeContextMenu(nodeContextMenu: UsePopoverContextMenuReturn) {
    const state = nodeContextMenu.state;
    const node = state.extraData;
    if (!node) return null;
    if (!state.open) return null;

    const {type} = node;

    if (!type) return null;

    const features = nodeFeatures[type];
    const ContextMenu = features?.ContextMenu;
    if (!ContextMenu) return null;

    return <ContextMenu popoverContextMenu={nodeContextMenu} node={node} />;
  }

  function renderNodeCreateDialog(dialogState: UseDialogStateReturn) {
    if (!dialogState.open) return null;
    const {type} = dialogState.extraData || {};
    if (!type) return null;

    const features = nodeFeatures[type];
    const CreateDialog = features?.CreateDialog;
    if (!CreateDialog) return null;

    return <CreateDialog dialogState={dialogState} />;
  }

  function renderNodeEditDialog(dialogState: UseDialogStateReturn) {
    const node = dialogState.extraData;
    if (!node) return null;
    if (!dialogState.open) return null;

    const {type} = node;

    if (!type) return null;

    const features = nodeFeatures[type];
    const EditDialog = features?.EditDialog;
    if (!EditDialog) return null;

    return <EditDialog dialogState={dialogState} />;
  }

  // Handle edge selection
  const onSelectionChange = useCallback((params: any) => {
    setSelectedEdges(params.edges || []);
  }, []);

  ///// return component
  return (
    <div className={cn(props.className, 'relative h-[90vh] w-full')}>
      {/* Render Swimlanes */}
      <Swimlanes />

      {/* Render Timeline Markers */}
      <TimelineMarkers />

      {/* React flow */}
      <ReactFlow
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onBeforeDelete={onBeforeDelete}
        // onEdgesDelete={onEdgesDelete}
        onNodesDelete={onNodesDelete}
        deleteKeyCode={['Backspace', 'Delete']}
        // deleteKeyCode={[]}
        onContextMenu={e => {
          // prevent bugs that context menu is shown when dialog is open
          if (!editDialogState.open && !createDialogState.open) wfContextMenu.onContextMenu(e);
        }} // for show context menu
        connectionMode={ConnectionMode.Strict}
        viewport={viewport}
        onViewportChange={setViewport}
        onNodeDoubleClick={onNodeDoubleClick} // for show edit dialog
        onNodeContextMenu={onNodeContextMenu} // for show node's context menu
        onEdgeContextMenu={onEdgeContextMenu}
        // Take snapshot after user has finished moving nodes
        onNodeDragStop={() => takeSnapshot()}
        // Take snapshot after node has been resized
        // onNodeResize={() => takeSnapshot()}
        onSelectionChange={onSelectionChange}        
        {...props.reactflowProps}>
        <MyControls />
        <MiniMap />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        <WorkflowContextMenu
          popoverContextMenu={wfContextMenu} // for context menu control itself
          // openNodeCreateDialog={(type, position) => createDialogState.openDialog({type, position})} //for menu show create dialog
        />
        {renderNodeCreateDialog(createDialogState)} {/*for show create dialog*/}
        {renderNodeEditDialog(editDialogState)} {/*for show edit dialog (when double click)*/}
        {renderNodeContextMenu(nodeCtxMenu)} {/*for show node's context menu*/}
        {renderEdgeContextMenu(edgeCtxMenu, selectedEdges, onEdgeStyleChange)}
      </ReactFlow>
    </div>
  );
}

export default ProjectWorkflow;
